import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useFeatureFlags } from 'libs.flags.react';
import { studyConfigOptsIntoNativeDcr, LibraryEndpoint } from 'libs.react';
import { ApiClientService, useApiClient } from 'libs.react_internal';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { EnvironmentsContext } from '../../contexts/environments';
import { useCurrentEnvironment } from '../use_current_environment';
/**
 * Whether this environment serves data change requests in the app, which the study configuration
 * deployed here decides. Everyone else keeps using the classic external app. Whether the connector
 * deployed alongside it is new enough to serve them is not checked yet.
 */
export const useNativeDcrAccess = () => {
    const { environment } = useCurrentEnvironment();
    const { isLoading: isLoadingEnvironments } = useContext(EnvironmentsContext);
    const { isFeatureEnabled } = useFeatureFlags();
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const isFeatureFlagEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.NATIVE_DATA_CHANGE_REQUESTS);
    /** The study configuration this environment was deployed with, rather than the one being edited */
    const studyConfigResourceId = environment?.metadata?.packages?.find(({ type }) => type === 'studyConfig')?.resourceId;
    const { data: studyConfig, isLoading: isLoadingStudyConfig } = useQuery({
        queryKey: ['study_config', 'deployed', studyConfigResourceId],
        queryFn: async () => {
            const searchParams = new URLSearchParams({ id: String(studyConfigResourceId) });
            const { data: response } = await libraryClient.get(`${LibraryEndpoint.GET_STUDY_CONFIGS}?${searchParams.toString()}`);
            return response.data?.[0] ?? null;
        },
        enabled: isFeatureFlagEnabled && !!studyConfigResourceId,
    });
    return {
        isNativeDcrEnabled: isFeatureFlagEnabled && studyConfigOptsIntoNativeDcr(studyConfig?.data),
        isLoading: isLoadingEnvironments || isLoadingStudyConfig,
    };
};
